import { useDisableMfa, useEnableMfa, useGetMfaStatus } from '@qse/api'
import { useUserStore } from '@qse/stores'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'

import { authService } from './auth-service'

export interface TwoFAStatus {
  isEnabled: boolean
  isConfigured: boolean
  preferred: string | null
}

export const use2FAManagement = () => {
  const [status, setStatus] = useState<TwoFAStatus>({
    isEnabled: false,
    isConfigured: false,
    preferred: null,
  })
  const [isCheckingStatus, setIsCheckingStatus] = useState(true)

  const { mutate: enableMfa, isPending: isEnabling } = useEnableMfa()
  const { mutate: disableMfa, isPending: isDisabling } = useDisableMfa()
  const { refetch: refetchMfaStatus } = useGetMfaStatus()
  const { updateMfaStatus } = useUserStore()

  // Check 2FA status using new API endpoint
  const check2FAStatus = useCallback(async () => {
    try {
      setIsCheckingStatus(true)
      const result = await refetchMfaStatus()

      if (result.data) {
        const currentStatus = {
          isEnabled: result.data.mfaStatus === 'enabled',
          isConfigured: result.data.mfaStatus === 'enabled',
          preferred: result.data.mfaStatus === 'enabled' ? 'TOTP' : null,
        }

        setStatus(currentStatus)
        updateMfaStatus(result.data.mfaStatus)

        return result.data.mfaStatus
      }
    } catch (error) {
      console.error('Error checking 2FA status:', error)
      toast.error('Failed to check 2FA status')
    } finally {
      setIsCheckingStatus(false)
    }
  }, [refetchMfaStatus])

  // Enable 2FA using new API endpoint
  const enable2FA = useCallback(async () => {
    enableMfa(undefined, {
      onSuccess: async () => {
        await check2FAStatus()
        toast.success('Two-Factor Authentication enabled successfully')
      },
      onError: error => {
        console.error('Error enabling 2FA:', error)
        toast.error('Failed to enable Two-Factor Authentication')
      },
    })

    return true
  }, [check2FAStatus, enableMfa])

  // Disable 2FA using Cognito verification first, then backend update
  const disable2FA = useCallback(
    async (totpCode: string) => {
      try {
        // First verify TOTP code with Cognito
        await authService.disableTOTP(totpCode)

        // Then update backend status
        disableMfa(undefined, {
          onSuccess: async () => {
            await check2FAStatus()
          },
          onError: error => {
            console.error('Error updating backend MFA status:', error)
            // Don't show error toast since Cognito verification succeeded
          },
        })

        return true
      } catch (error) {
        console.error('Error disabling 2FA:', error)
        toast.error('Failed to disable Two-Factor Authentication')

        return false
      }
    },
    [check2FAStatus, disableMfa],
  )

  // Setup 2FA (legacy method for TOTP setup)
  const setup2FA = useCallback(async () => {
    try {
      const totpSetup = await authService.setupTOTP()

      return totpSetup
    } catch (error) {
      console.error('Error setting up 2FA:', error)

      return null
    }
  }, [])

  // Verify 2FA setup (legacy method for TOTP verification)
  const verify2FASetup = useCallback(
    async (totpCode: string) => {
      try {
        await authService.verifyTOTP(totpCode)
        // Update MFA status to enabled only after successful verification
        enableMfa(undefined, {
          onSuccess: async () => {
            await check2FAStatus()
          },
          onError: error => {
            console.error('Error enabling MFA after verification:', error)
          },
        })

        return true
      } catch (error) {
        console.error('Error verifying 2FA setup:', error)

        return false
      }
    },
    [check2FAStatus, enableMfa],
  )

  // Check status on mount
  useEffect(() => {
    check2FAStatus()
  }, [check2FAStatus])

  return {
    status,
    isLoading: isEnabling || isDisabling,
    isCheckingStatus,
    check2FAStatus,
    enable2FA,
    disable2FA,
    setup2FA,
    verify2FASetup,
  }
}
